<template>
  <teleport to="body">
    <div class="message-overlay" v-if="visible" @click.self="onMaskClick">
      <div class="message-box" :class="type">
        <div class="message-icon">
          <i class="icon" :class="iconClass"></i>
        </div>
        <div class="message-content">
          <h3 class="title" v-if="title">{{ title }}</h3>
          <p class="message">{{ message }}</p>
        </div>
        <div class="message-actions">
          <button 
            v-if="showCancel" 
            class="btn cancel-btn" 
            @click="onCancel"
          >
            {{ cancelText }}
          </button>
          <button 
            class="btn confirm-btn" 
            :class="type"
            @click="onConfirm"
          >
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
export default {
  name: 'MessageBox',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'info',
      validator: value => ['info', 'success', 'warning', 'error', 'confirm'].includes(value)
    },
    confirmText: {
      type: String,
      default: '确定'
    },
    cancelText: {
      type: String,
      default: '取消'
    },
    showCancel: {
      type: Boolean,
      default: false
    },
    closeOnMaskClick: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    iconClass() {
      switch (this.type) {
        case 'success':
          return 'success-icon';
        case 'warning':
          return 'warning-icon';
        case 'error':
          return 'error-icon';
        case 'confirm':
          return 'confirm-icon';
        default:
          return 'info-icon';
      }
    }
  },
  methods: {
    onConfirm() {
      this.$emit('confirm');
    },
    onCancel() {
      this.$emit('cancel');
    },
    onMaskClick() {
      if (this.closeOnMaskClick) {
        this.$emit('cancel');
      }
    }
  }
}
</script>

<style>
.message-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999999;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.message-box {
  width: 320px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 24px;
  animation: slideIn 0.3s ease-out;
  position: relative;
  z-index: 10000000;
}

@keyframes slideIn {
  from { transform: translateY(-20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.message-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.icon {
  width: 48px;
  height: 48px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
}

.info-icon {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%231890ff' viewBox='0 0 24 24'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z'/%3E%3C/svg%3E");
}

.success-icon {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%2352c41a' viewBox='0 0 24 24'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/%3E%3C/svg%3E");
}

.warning-icon {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23faad14' viewBox='0 0 24 24'%3E%3Cpath d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z'/%3E%3C/svg%3E");
}

.error-icon {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23f5222d' viewBox='0 0 24 24'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z'/%3E%3C/svg%3E");
}

.confirm-icon {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23faad14' viewBox='0 0 24 24'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z'/%3E%3C/svg%3E");
}

.message-content {
  text-align: center;
  margin-bottom: 24px;
}

.title {
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.message {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

.message-actions {
  display: flex;
  justify-content: space-around;
  gap: 12px;
}

.btn {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
  outline: none;
}

.cancel-btn {
  background-color: #f5f5f5;
  color: #666;
  border: 1px solid #d9d9d9;
}

.cancel-btn:hover {
  background-color: #e8e8e8;
  color: #333;
}

.confirm-btn {
  color: #fff;
}

.confirm-btn.info {
  background-color: #1890ff;
}

.confirm-btn.info:hover {
  background-color: #40a9ff;
}

.confirm-btn.success {
  background-color: #52c41a;
}

.confirm-btn.success:hover {
  background-color: #73d13d;
}

.confirm-btn.warning {
  background-color: #faad14;
}

.confirm-btn.warning:hover {
  background-color: #ffc53d;
}

.confirm-btn.error {
  background-color: #f5222d;
}

.confirm-btn.error:hover {
  background-color: #ff4d4f;
}

.confirm-btn.confirm {
  background-color: #1890ff;
}

.confirm-btn.confirm:hover {
  background-color: #40a9ff;
}
</style> 